import React, { useMemo } from "react";
import {
  FaCheckCircle,
  FaRedoAlt,
  FaTimesCircle,
  FaTrophy,
} from "react-icons/fa";

function getScoreMessage(score, total) {
  if (!total) return "Aucune question terminée.";

  const ratio = score / total;

  if (ratio === 1) return "Parfait, aucune erreur sur cette série.";
  if (ratio >= 0.8) return "Très bon résultat, les bases sont solides.";
  if (ratio >= 0.5)
    return "Bon entraînement, quelques techniques sont à revoir.";

  return "Continue à t’entraîner, ce quizz est fait pour progresser.";
}

export default function QuizResults({
  questions = [],
  errors = [],
  score = 0,
  onRestart,
}) {
  const errorMap = useMemo(() => {
    return new Map(
      errors.map((error) => [
        error.questionId || error.question?.id,
        error.selected,
      ])
    );
  }, [errors]);

  const total = questions.length;
  const scorePercent = total ? Math.round((score / total) * 100) : 0;

  return (
    <div className="space-y-5">
      <div className="relative overflow-hidden rounded-[2rem] bg-slate-950 p-5 text-white shadow-xl shadow-slate-300/60 dark:shadow-black/20 sm:p-7">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.35),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(168,85,247,0.35),transparent_35%)]" />

        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white/85">
              <FaTrophy />
              Résultats
            </div>

            <h2 className="mt-4 text-2xl font-bold sm:text-3xl">
              {score} / {total}
            </h2>

            <p className="mt-2 max-w-xl text-sm leading-6 text-white/78">
              {getScoreMessage(score, total)}
            </p>
          </div>

          <div className="flex flex-col items-start gap-3 sm:items-end">
            <div className="rounded-[1.5rem] border border-white/15 bg-white/10 px-4 py-3 backdrop-blur">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/60">
                Réussite
              </p>

              <p className="text-2xl font-bold">{scorePercent}%</p>
            </div>

            <button
              type="button"
              onClick={onRestart}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-white px-5 text-sm font-bold text-slate-950 shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl"
            >
              <FaRedoAlt />
              Recommencer
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {questions.map((question, index) => {
          const userAnswer = errorMap.get(question.id);
          const isCorrect = !userAnswer;

          return (
            <article
              key={`${question.id}-${index}`}
              className={[
                "overflow-hidden rounded-[1.5rem] border bg-white shadow-md dark:bg-slate-950",
                isCorrect
                  ? "border-emerald-200 shadow-emerald-100/70 dark:border-emerald-900/70 dark:shadow-black/10"
                  : "border-rose-200 shadow-rose-100/70 dark:border-rose-900/70 dark:shadow-black/10",
              ].join(" ")}
            >
              <div className="relative bg-slate-50 p-2 dark:bg-slate-900">
                <div
                  className="mx-auto w-full max-w-[210px]"
                  style={{ aspectRatio: "551 / 617" }}
                >
                  <img
                    src={question.image}
                    alt={question.correctAnswer}
                    className="h-full w-full object-contain"
                    loading="lazy"
                  />
                </div>

                <div
                  className={[
                    "absolute left-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full text-white shadow-lg",
                    isCorrect ? "bg-emerald-500" : "bg-rose-500",
                  ].join(" ")}
                >
                  {isCorrect ? <FaCheckCircle /> : <FaTimesCircle />}
                </div>
              </div>

              <div className="space-y-2 p-3 text-sm">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                  Question {index + 1}
                </p>

                {isCorrect ? (
                  <p className="font-bold text-emerald-700 dark:text-emerald-300">
                    {question.correctAnswer}
                  </p>
                ) : (
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Ta réponse
                    </p>

                    <p className="font-bold text-rose-700 line-through dark:text-rose-300">
                      {userAnswer}
                    </p>

                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Bonne réponse
                    </p>

                    <p className="font-bold text-emerald-700 dark:text-emerald-300">
                      {question.correctAnswer}
                    </p>
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}